import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import SvgColor from 'src/components/svg-color';
import { ROLES, USER_TYPE_CONTRACTOR, USER_TYPE_SUPER_ADMIN, USER_TYPE_TECHNICIAN } from 'src/config-global';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { useSupportCounts } from 'src/hooks/use-support-counts';
import { paths } from 'src/routes/paths';
import { useGetUserProfileQuery } from 'src/store/Reducer/settings';
import { isSocketConnected, subscribePlatformAccess, subscribeUnreadCount } from 'src/utils/socket';

// Helper to simplify icon rendering
const icon = (name) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />;

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  support: icon('ic_support'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  trainers: icon('ic-trainers'),
  customers: icon('ic-customers'),
  languages: icon('ic-languages'),
  faq: icon('ic_faq'),
  specializations: icon('ic-specializations'),
  trainingModes: icon('ic-trainingMode'),
  inventory: icon("ic_inventory"),
  settings: icon("ic_setting"),
  template: icon("ic_template"),
  worker: icon("ic_worker"),
  trainingAcademy: icon("ic_training_academy"),
  supportCenter: icon("ic_support_center"),
  buildMode: icon("mdi:truck-outline"),
  notification: icon('ic_notification')
};

export function useNavData() {
  const { user } = useMockedUser();
  const isAdmin = user?.user_type === USER_TYPE_SUPER_ADMIN;
  const isTechnician = user?.user_type === USER_TYPE_TECHNICIAN;
  const { data: userProfile, isLoading, refetch } = useGetUserProfileQuery({ role: 'contractor' }, { skip: !user?._id || isAdmin });
  const { pathname } = useLocation();
   const profilePlatformAccess = useMemo(() => {
    if(userProfile?.body?.platform_access?.length > 0){
      return userProfile?.body?.platform_access;
    }
  }, [userProfile]);


  const [unReadMessageCount, setUnReadMessageCount] = useState(0);
  const [updatedPlatformAccess, setUpdatedPlatformAccess] = useState(profilePlatformAccess || user?.platform_access || []);
    // keep a ref to the latest user so socket callbacks never see a stale closure
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

    // reset local socket-driven state whenever the underlying user (or their base access) changes
  useEffect(() => {
    if(profilePlatformAccess){
      setUpdatedPlatformAccess(profilePlatformAccess);
    }else{
      setUpdatedPlatformAccess(user?.platform_access || []);
    }
  }, [user?._id, user?.platform_access , profilePlatformAccess]);


  useEffect(() => {
    let cancelled = false;
    let unsubscribe;
    let unsubscribePlatformAccess;

    const initialize = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (cancelled) return;

      unsubscribe = subscribeUnreadCount((count) => {
        setUnReadMessageCount(count);
      });

      unsubscribePlatformAccess = subscribePlatformAccess((platformObject) => {
        if (userRef.current?._id?.toString() === platformObject?.user_id?.toString()) {
          setUpdatedPlatformAccess(platformObject?.platform_access);
        }
      });
    };

    initialize();

    return () => {
      cancelled = true;
      unsubscribe?.();
      unsubscribePlatformAccess?.();
    };
  }, []);

  const isContractor = user?.user_type === USER_TYPE_CONTRACTOR;
  const full_access = user?.role === ROLES?.FULL_ACCESS;
  const view_access = user?.role === ROLES?.VIEW;

  const platform_access = useMemo(() => {
    if (user?.user_type === "technician" && user?.contractor) {
      return user?.contractor?.platform_access || ["support_center"];
     } 
   if (updatedPlatformAccess?.length > 0) {
      return updatedPlatformAccess;
    }
    return user?.platform_access || ["support_center"];
  }, [user , updatedPlatformAccess]);

  const { is_training_academy, is_customer_hub } = useMemo(() => ({
    is_training_academy: platform_access?.includes('training_academy'),
    is_customer_hub: platform_access?.includes('customer_hub'),
  }), [platform_access]);

  const { supportCount, contactUsCount } = useSupportCounts({ skip: !isAdmin });

  const paymentPermission = useMemo(() => {
    if (!userProfile) return false;
    const payment_role = userProfile?.body?.payment_role;
    if (user?.user_type === USER_TYPE_CONTRACTOR) return true;
    if (user?.user_type === USER_TYPE_TECHNICIAN && payment_role === "collect_only") return true;
  }, [userProfile, user?.user_type])

  const versionSetting = useMemo(() => {
    if(userProfile?.body?.versionSetting){
      return userProfile?.body?.versionSetting;
    }
  },
  [userProfile?.body?.versionSetting]);

  useEffect(() => {
      if(isAdmin) return;
      refetch();
  }, [pathname]);

  // SuperAdmin Navigation Items
  const superAdminNav = [
    { title: 'Dashboard', path: paths.superAdminDashboard.root, icon: ICONS.dashboard },
    { title: 'AI Chat', path: paths.superAdminDashboard.aiChat, icon: ICONS.chat },
    { title: 'Admin Management', path: paths.superAdminDashboard.adminManagement, icon: <Iconify icon="heroicons-solid:users" /> },
    { title: 'Contractor', path: paths.superAdminDashboard.contractor.root, icon: ICONS.user },
    { title: 'Clients', path: paths.superAdminDashboard.customers.root, icon: ICONS.customers },
    { title: 'Estimate Templates', path: paths.superAdminDashboard.estimatorTemplates.root, icon: ICONS.template },
    {
      title: 'Estimate Settings',
      path: paths.superAdminDashboard.estimatorSetting.root,
      icon: ICONS.settings,
      children: [
        { title: 'Geographic Adjuster', path: paths.superAdminDashboard.estimatorSetting.geographicAdjuster },
        { title: 'Primary Service', path: paths.superAdminDashboard.estimatorSetting.primaryService },
        { title: 'Additional Service', path: paths.superAdminDashboard.estimatorSetting.additionalService },
        { title: 'Terms & conditions', path: paths.superAdminDashboard.estimatorSetting.termsAndConditions },
        { title: 'Units', path: paths.superAdminDashboard.estimatorSetting.metric },
        { title: 'Invoice Notes', path: paths.superAdminDashboard.estimatorSetting.invoiceNotes },
        { title: 'Estimate Notes', path: paths.superAdminDashboard.estimatorSetting.estimateNotes },
      ]
    },
    { title: 'Jobs', path: paths.superAdminDashboard.jobs.root, icon: ICONS.job },
    { title: 'Job Type', path: paths.superAdminDashboard.jobType.root, icon: ICONS.ecommerce },
    { title: 'Invoices', path: paths.superAdminDashboard.invoices.root, icon: ICONS.invoice },
    { title: 'Technicians', path: paths.superAdminDashboard.technicians.root, icon: ICONS.worker },
    {
      title: 'Requests',
      path: paths.superAdminDashboard.requests.root,
      icon: ICONS.support,
      children: [
        { title: 'Contact Support', path: paths.superAdminDashboard.requests.contactSupport, info: <Label color="primary">{supportCount ?? 0}</Label> },
        { title: 'Contact Us', path: paths.superAdminDashboard.requests.contactUs, info: <Label color="primary">{contactUsCount ?? 0}</Label> }
      ]
    },

    {
      title: 'Training Academy',
      path: paths.superAdminDashboard.trainingAcademy.root,
      icon: ICONS.trainingAcademy,
      children: [
        { title: 'Alerts', path: paths.superAdminDashboard.trainingAcademy.alerts },
        { title: 'Categories', path: paths.superAdminDashboard.trainingAcademy.categories },
        { title: 'Courses', path: paths.superAdminDashboard.trainingAcademy.courses }
      ]
    },
    {
      title: 'Support Center',
      path: paths.superAdminDashboard.supportCenter.root,
      icon: <Iconify icon="wpf:books" />,
      children: [
        { title: 'Users', path: paths.superAdminDashboard.supportCenter.user },
        { title: 'Categories', path: paths.superAdminDashboard.supportCenter.categories },
        { title: 'Resource Packages', path: paths.superAdminDashboard.supportCenter.resourcePackages },
        { title: 'Resources', path: paths.superAdminDashboard.supportCenter.resources }
      ]
    },
    {
      title: "Build Mode",
      path: paths.superAdminDashboard.buildMode.root,
      icon: <Iconify icon="mdi:truck-outline" />,
      children: [
        {
          title: "Build List",
          path: paths.superAdminDashboard.buildMode.buildList,
          // info: <Label color="primary">7</Label>
        },
        {
          title: "Build Stages",
          path: paths.superAdminDashboard.buildMode.buildStages
        },
        {
          title: "Shop Admins",
          path: paths.superAdminDashboard.buildMode.shopAdmins
        },
        {
          title: "Build Board",
          path: paths.superAdminDashboard.buildMode.shopFloorDisplay,
        },
        {
          title: "Message Board",
          path: paths.superAdminDashboard.buildMode.messageBoard,
          info: <Label color="primary">{unReadMessageCount ?? 0}</Label>
        },
        {
          title: "Notifications",
          path: paths.superAdminDashboard.buildMode.notifications,
          // info: <Label color="primary">5</Label>
        }
      ]
    },
    // { title: 'Settings', path: paths.superAdminDashboard.settings, icon: ICONS.settings },
    {
      title: 'Settings',
      path: paths.superAdminDashboard.settings.root,
      icon: ICONS.settings,
      children: [
        { title: 'General', path: paths.superAdminDashboard.settings.general },
        { title: 'Customize Pages', path: paths.superAdminDashboard.settings.customizePages },
      ]
    },
    { title: 'Terms And Conditions', path: paths.superAdminDashboard.termsAndConditions, icon: ICONS.file },
    { title: 'About Us', path: paths.superAdminDashboard.aboutUs, icon: ICONS.tour },
    { title: 'Privacy Policy', path: paths.superAdminDashboard.privacyPolicy, icon: ICONS.lock },
    { title: 'FAQs', path: paths.superAdminDashboard.faqs, icon: ICONS.faq },
    { title: 'User Manual', path: paths.superAdminDashboard.userManual, icon: ICONS.file }
  ];

  // Contractor Hub Navigation Items
  const contractorHubNav = [
    ...(is_customer_hub ? [ // Show routes only if is_customer_hub is true
      { title: 'Dashboard', path: paths.dashboard.root, icon: ICONS.dashboard },
      { title: 'Clients', path: paths.dashboard.customers.root, icon: ICONS.customers },
      { title: 'Jobs', path: paths.dashboard.jobs.root, icon: ICONS.job },
      ...(full_access ? [
        { title: 'Invoices', path: paths.dashboard.invoices.root, icon: ICONS.invoice }
      ] : []),
      ...(paymentPermission && versionSetting?.v3 ? [
        { title: 'Transactions', path: paths.dashboard.transactions.root, icon: ICONS.order }
      ] : []),
      ...(!view_access ? [
        { title: 'Inventory', path: paths.dashboard.inventory.root, icon: ICONS.inventory },
        { title: 'Technicians', path: paths.dashboard.technicians.root, icon: ICONS.worker },
        { title: 'Estimate Templates', path: paths.dashboard.estimatorTemplates.root, icon: ICONS.template }
      ] : [])
    ] : [
      {
        title: 'Customer Hub',
        path: paths.marketing.page('customer-hub'),
        icon: <Iconify icon="mdi:lock-outline" />,
      }
    ]),
     ...(versionSetting?.v2 ? [
      {
        title: 'Training Academy',
        path: (!view_access && (is_training_academy)) ? paths.dashboard.trainingAcademy.root : paths.marketing.page('training-academy'),
        icon: (!view_access && (is_training_academy)) ? ICONS.trainingAcademy : <Iconify icon="mdi:lock-outline" />,
      },

    ...(!view_access ? [{
        title: 'Support Center',
        path: paths.dashboard.supportCenter.root,
        icon: <Iconify icon="wpf:books" />,
        children: [
          { title: 'Resources', path: paths.dashboard.supportCenter.resources },
        ]
       }] : []) 
     ]:[]),
     ...(versionSetting?.v4 ? [
        ...(!view_access && is_customer_hub  ? [{
          title: 'Build Mode',
          path: paths.dashboard.buildMode.root,
          icon: <Iconify icon="mdi:truck-outline" />,
          children: [
            {
              title: 'Build Update', path: paths.dashboard.buildMode.buildList,
            },
            {
              title: "Message Board", path: paths.dashboard.buildMode.messageBoard,
              info: <Label color="primary">{unReadMessageCount}</Label>
            },
          ]
      }] : []),
     ]:[]),

    ...(!view_access ? [{
      title: 'Shop 3R',
      path: 'https://3rpressurewashers.com/',
      target: '_blank',
      icon: <Iconify icon="mdi:storefront-outline" />
    }] : []),
    ...(is_customer_hub && !view_access ? [{
      title: 'Settings',
      path: paths.dashboard.settings.root,
      icon: ICONS.settings,
      children: [
        { title: 'General', path: paths.dashboard.settings.root },
        ...(versionSetting?.v3 ? [{ title: 'Payment', path: paths.dashboard.settings.payment }] : []),
        {
          title: 'Estimate Settings',
          path: paths.dashboard.settings.estimatorSetting.root,
          children: [
            { title: 'Geographic Adjuster', path: paths.dashboard.settings.estimatorSetting.geographicAdjuster },
            { title: 'Units', path: paths.dashboard.settings.estimatorSetting.metric },
            { title: 'Primary Service', path: paths.dashboard.settings.estimatorSetting.primaryService },
            { title: 'Additional Service', path: paths.dashboard.settings.estimatorSetting.additionalService },
            { title: 'Terms & Conditions', path: paths.dashboard.settings.estimatorSetting.termsAndConditions },
            { title: 'Job Type', path: paths.dashboard.settings.estimatorSetting.jobType },
            { title: 'Invoice Notes', path: paths.dashboard.settings.estimatorSetting.invoiceNotes },
            { title: 'Estimate Notes', path: paths.dashboard.settings.estimatorSetting.estimateNotes },
          ]
        }
      ]
    }] : []),
    { title: 'Contact/Help', path: paths.dashboard.support, icon: ICONS.support },
    { title: 'User Manual', path: paths.superAdminDashboard.userManual, icon: ICONS.file }
  ];

  const ShopAdminNav = [
    { title: "Dashboard", path: paths.shopAdminDashboard.root, icon: ICONS.dashboard },
    {
      title: "Build List", path: paths.shopAdminDashboard.buildList, icon: <Iconify icon="mdi:truck-outline" />,
    },
    {
      title: "Notifications", path: paths.shopAdminDashboard.notifications, icon: ICONS.notification,
    },
    {
      title: "Message Board", path: paths.shopAdminDashboard.messageBoard, icon: ICONS.chat,
      info: <Label color="primary">{unReadMessageCount}</Label>
    },
    { title: "User Manual", path: paths.superAdminDashboard.userManual, icon: ICONS.file },
  ]

  const data = useMemo(() => [
    { subheader: 'Dashboard', items: isAdmin ? superAdminNav : (isContractor || isTechnician)  ? contractorHubNav : ShopAdminNav }
  ], [isAdmin, isContractor, isTechnician, paymentPermission, unReadMessageCount , platform_access ,versionSetting]);

  return data;
}
